import { forwardRef, useId, type SelectHTMLAttributes } from 'react'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'children'> {
  label: string
  options: readonly SelectOption[]
  hint?: string | undefined
  error?: string | undefined
  /** Libellé visible masqué (barre d'outils dense) : le nom accessible reste. */
  hideLabel?: boolean
}

/**
 * Liste déroulante native avec libellé, aide et erreur, sur le modèle d'`Input`. Le `<select>` natif reste le
 * meilleur choix au toucher et au clavier ; ce composant ne fait qu'en fixer l'habillage et les attributs.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, id: providedId, label, options, hint, error, hideLabel = false, ...props },
  ref,
) {
  const generatedId = useId()
  const id = providedId ?? generatedId
  const messageId = hint || error ? `${id}-message` : undefined

  return (
    <div className={mergeClassNames('ds-field', hideLabel && 'ds-field--inline')}>
      <label className={mergeClassNames('ds-field__label', hideLabel && 'sr-only')} htmlFor={id}>
        {label}
      </label>
      <select
        ref={ref}
        id={id}
        className={mergeClassNames('ds-input', 'ds-select', error && 'ds-input--error', className)}
        aria-describedby={messageId}
        aria-invalid={Boolean(error)}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? (
        <span id={messageId} className="ds-field__message ds-field__message--error">
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className="ds-field__message">
          {hint}
        </span>
      ) : null}
    </div>
  )
})
